import { env } from '@/env';
import type { ConsentUserMetadataType } from '@/consts/consentTypes';
import type {
  UserData,
  SolutionType,
  PromoCodeResponse,
  MedicareCoverageData,
  TrackType,
} from '@/types/registration';
import { getAppNameByPhoneType, vendorID, deviceModel } from '@/utils/device';
// Only the download-SMS call is pilot-mocked on staging. The failed-registration
// call is mocked on the dev server only (RND-38229), so the F&F staging host
// records a real CAP_REACHED instead of reporting success and storing nothing,
// and the e2e harness can intercept the real request. shippingDetails / promoCode / signup deliberately always hit
// hello-server: F&F testing on staging needs real accounts and real shipping
// records (RND-37449). CMS mocking lives in cmsApiClient.ts.
import {
  isDevServer,
  isPilotMock,
  MOCK_DOWNLOAD_SMS_RESPONSE,
  mockRecordFailedRegistration,
} from '@/services/pilotMock';
import axios, { AxiosInstance } from 'axios';

const BASE_URL = env.HELLO_SERVER_URL;
const BASE_API_URL = `${BASE_URL}/hs/`;
const TOKEN = env.HELLO_SERVER_TOKEN;

const SHIPPING_DETAILS_URL = 'adminapi/1.0/shippings/details';
const PROMO_CODE_URL = 'api/1.0/premium/codes/';
const USER_URL = 'api/1.0/users';
const DOWNLOAD_SMS_URL = 'api/2.0/sms/sendDownloadAppSMS';
const PHONE_OTP_START_URL = 'api/2.0/users/auths/phone/start';
const PHONE_OTP_CHECK_URL = 'api/2.0/users/auths/phone/checkCode';
const CONSENTS_URL = 'api/2.0/userMetadata/consents';

export type Locale = 'en' | 'es';

export interface RegistrationMetadata {
  webregUserId: string;
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  selectedTracks?: TrackType[];
}

export interface ConsentMetadata {
  version: number;
  status: 'consent' | 'revoked';
  dateReported: number;
  source: string;
  userMetadataObjectType: ConsentUserMetadataType;
  iteration?: number;
}

export interface ConsentsMetadataRequest {
  consents: ConsentMetadata[];
}

export interface PersonalDetails {
  firstname: string;
  lastname: string;
  ['date_of_birth']: string;
  city: string;
  phonenumber: string;
  state: string;
  streetaddress: string;
  // Required by hello-server: '' is rejected with ErrorCode(1000, Bad request).
  // See the placeholder pair in OtpAccountStep for how signup satisfies this
  // before the patient has entered an address (RND-37449).
  zipcode: string;
}

export interface CmsAccessEnrollment {
  tracks: TrackType[];
}

export interface SignupPayload {
  password: string;
  email?: string;
  phoneNumber: string;
  personalDetails: PersonalDetails;
  eligibilityFields?: MedicareCoverageData | null;
  enableSmsMarketing: boolean;
  registrationMetadata: RegistrationMetadata;
  locale: Locale;
  promoCode: string;
  sampleGroup: string;
  privacyPracticeReviewed?: boolean;
  consentToShareData?: boolean;
  consentsMetadata?: ConsentsMetadataRequest;
  cmsAccessEnrollment?: CmsAccessEnrollment;
}

export interface SignupResponse {
  authToken: string;
  user: {
    email: string;
    analyticsID: string;
    dateCreated: number;
    personalDetails: PersonalDetails;
    accountValidated: boolean;
  };
  promoCode: PromoCodeResponse;
  otpLink?: string;
}

export interface PhoneCodeCheckResponse {
  valid: boolean;
}

export interface ShippingDetailsPayload {
  firstName: string;
  lastName: string;
  streetaddress: string;
  city: string;
  state: string;
  zipcode: string;
  phonenumber: string;
  cuffSize: string;
  solutions: SolutionType[];
}

export interface ShippingDetailsResponse {
  'has-glucose-solution': string;
  'promo-code': string;
  clientID: string;
  'has-bp-shipping-filled': string;
  'has-weight-device-shipping': string;
  'has-bp-device-type': string;
  'has-glucose-device-shipping': string;
  'has-bp-device': string;
  'has-activity-device': string;
  'has-bp-solution': string;
  'has-glucose-device': string;
  'has-bp-device-shipping': string;
  'company-name': string;
  'has-activity-device-shipping': string;
  privacyPracticeReviewed: string;
  'has-weight-device': string;
  language: string;
  'has-activity-solution': string;
  sampleGroup: string;
  'user-source': string;
  enableSmsMarketing: string;
  consentToShareData: string;
  'enrolment-type': string;
  'has-weight-solution': string;
}

class ApiClient {
  private axiosInstance: AxiosInstance;

  constructor(baseURL: string) {
    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add authorization token to all requests except shipping details
    this.axiosInstance.interceptors.request.use((config) => {
      const modifiedConfig = { ...config };
      if (modifiedConfig.url !== SHIPPING_DETAILS_URL) {
        modifiedConfig.headers.Authorization = `Bearer ${TOKEN}`;
      }
      return modifiedConfig;
    });
  }

  async shippingDetails(
    shippingDetails: ShippingDetailsPayload,
    userData: UserData,
  ): Promise<ShippingDetailsResponse> {
    const { authToken } = userData;
    const response = await this.axiosInstance.post(SHIPPING_DETAILS_URL, shippingDetails, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });
    return response.data;
  }

  async promoCode(code: string, token?: string): Promise<PromoCodeResponse> {
    const queryParams = new URLSearchParams();
    if (token) {
      queryParams.append('token', token);
    }
    const url = `${PROMO_CODE_URL}${code}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await this.axiosInstance.get(url);
    // A 200 is not proof of a promo code. A misrouted gateway answers the SPA's
    // index.html with 200 (the /hs prefix regression on 2026-08-06 did exactly
    // that), and the old code took it as a valid code: `capReached` and `code`
    // became undefined, signup then sent an empty promoCode, and the member met
    // a generic failure several screens later. Fail here instead (RND-38229).
    if (typeof (response.data as PromoCodeResponse | undefined)?.code !== 'string') {
      throw new Error(`promo code lookup returned no code for ${code}`);
    }
    return response.data;
  }

  async signup({
    password,
    email,
    phoneNumber,
    personalDetails,
    eligibilityFields,
    enableSmsMarketing,
    registrationMetadata,
    locale,
    promoCode,
    sampleGroup,
    privacyPracticeReviewed = false,
    consentToShareData = false,
    consentsMetadata,
    cmsAccessEnrollment,
  }: SignupPayload): Promise<SignupResponse> {
    const app = getAppNameByPhoneType();
    const properties = {
      locale,
      promoCode,
      sampleGroup,
      privacyPracticeReviewed,
      consentToShareData,
    };

    const response = await this.axiosInstance.post(USER_URL, {
      password,
      app,
      ...(email && { email }),
      phone: phoneNumber,
      securityAnswer: 'deprecated',
      securityQuestion: 'deprecated',
      vendorID,
      deviceModel,
      personalDetails,
      eligibilityFields,
      enableSmsMarketing,
      properties,
      registrationMetadata,
      ...(consentsMetadata && { consentsMetadata }),
      ...(cmsAccessEnrollment && { cmsAccessEnrollment }),
    });

    return response.data;
  }

  /**
   * Record consent metadata for an existing user. Signup carries consents for
   * anything known at account-creation time; this is the route for anything
   * answered afterwards, which is every consent in the 2.0 flow that sits on a
   * screen after the intake form.
   */
  async postConsents(consents: ConsentMetadata[], authToken: string): Promise<void> {
    await this.axiosInstance.post(
      CONSENTS_URL,
      { consents },
      { headers: { Authorization: `Bearer ${authToken}` } },
    );
  }

  /**
   * Send a phone-verification OTP via SMS. `phone` must already be E.164
   * (see toE164 in @/utils/phone). The Twilio send result is not useful to the
   * client, so it's discarded.
   */
  async startPhoneOtp(phone: string): Promise<void> {
    await this.axiosInstance.post(PHONE_OTP_START_URL, { phone });
  }

  /**
   * Check an OTP code's validity WITHOUT logging in or creating a user — the
   * registration-time counterpart to phone/verify (which is login-only and
   * fails when no user record exists yet). Issues no session token.
   * `phone` must already be E.164.
   */
  async checkPhoneOtp(phone: string, code: string): Promise<PhoneCodeCheckResponse> {
    const response = await this.axiosInstance.post(PHONE_OTP_CHECK_URL, { phone, code });
    return response.data;
  }

  async downloadSMS(phone: string, token: string, otpLink?: string): Promise<string> {
    if (isPilotMock()) return MOCK_DOWNLOAD_SMS_RESPONSE;
    // If we have a valid OTP link, use it directly
    if (otpLink) {
      try {
        new URL(otpLink); // Validate URL
        const response = await this.axiosInstance.post(
          otpLink,
          {
            action: 'popupSubmitDownloadAppPopup',
            phone,
            token,
            site: 'ACCESS_REGISTRATION',
          },
          { baseURL: '' }, // Use the OTP link as-is
        );
        return response.data;
      } catch (error) {
        // Fall through to regular endpoint if OTP link fails
      }
    }

    // Fallback to regular endpoint
    const response = await this.axiosInstance.post(DOWNLOAD_SMS_URL, {
      action: 'popupSubmitDownloadAppPopup',
      phone,
      token,
      site: 'ACCESS_REGISTRATION',
    });
    return response.data;
  }

  async recordFailedRegistration({
    webregUserId,
    identifier,
    reason,
    promoCode,
  }: {
    webregUserId: string;
    identifier: string;
    reason: 'CAP_REACHED';
    promoCode: string;
  }): Promise<{ status: string }> {
    // Dev server only. Not the e2e build: the Playwright harness intercepts this
    // request, so mocking it here would hide the one call RND-38229 exists to
    // make, and the cap path would be untestable end to end.
    if (isDevServer()) return mockRecordFailedRegistration();
    const response = await this.axiosInstance.post(
      `api/2.0/registration/${webregUserId}/failure`,
      {
        identifier,
        reason,
        promoCode,
      },
    );
    return response.data;
  }
}

export enum HelloServerErrorCodes {
  /**
   * hello-server's code 1000 is NOT a unique "user already exists"
   * discriminator — it is the generic bad-request code. Observed both on a
   * duplicate email/phone at signup and on payload validation failures (an
   * empty `zipcode` returns `ErrorCode(1000, Bad request)`, 2026-08-06). No
   * status/payload field is known to separate the two cases, so anything derived
   * from this code is labelled as the union of them rather than guessing. A
   * distinct duplicate-user code from hello-server is the real fix.
   */
  UserAlreadyExistsOrBadRequest = 1000,
  /**
   * hello-server's promo-code rung of the signup ladder, and the *secondary*
   * cap signal (RND-38229). The authoritative one is `capReached` on the
   * promo-code lookup, which arrives on a 200. No dedicated cap error code has
   * ever been named, and a *disabled* code returned HTTP 404 with no body
   * number on 2026-09-02 — a different case. So this is the promo rung, not
   * "cap"; the mapping lives in `signupPromoRejected` in useWregSignup.ts.
   */
  PromoCodeRejected = 1009,
  EligibilityRecordAttachedToUser = 14022,
}

export const apiClient = new ApiClient(BASE_API_URL);

export const DEFAULT_CUFF_SIZE = 'medium';
